import clsx from "clsx";
import { useState } from "react";
import type { PlayerAstral } from "../types";
import { getAstralWhiteIconPath, ASTRAL_RARITY_FILL } from "../utils/assets";
import { FormattedNumber } from "./FormattedNumber";
import { ModalBackdrop } from "./ModalBackdrop";

const RARITY_BORDER: Record<string, string> = {
  gray: "border-gray-400 text-gray-300",
  green: "border-green-500 text-green-400",
  blue: "border-blue-500 text-blue-400",
  purple: "border-purple-500 text-purple-400",
  orange: "border-orange-500 text-orange-400",
  red: "border-red-500 text-red-400",
};

const RARITY_GLOW: Record<string, string> = {
  gray: "shadow-gray-500/30",
  green: "shadow-green-500/40",
  blue: "shadow-blue-500/40",
  purple: "shadow-purple-500/40",
  orange: "shadow-orange-500/50",
  red: "shadow-red-500/50",
};

interface AstralDetailModalProps {
  astral: PlayerAstral;
  equipSlotCount?: number;
  onClose: () => void;
  onSell?: () => void;
  onEquip?: () => void;
  onUnequip?: () => void;
  onUpgrade?: () => void;
  actionLoading?: boolean;
}

const STAT_LABELS: Record<string, string> = {
  patk: "PATK",
  matk: "MATK",
  pdef: "PDEF",
  mdef: "MDEF",
  crit: "CRIT",
  hp: "HP",
  crit_damage_pct: "Crit damage",
  dodge_pct: "Dodge",
  damage_reduction_pct: "Damage reduction",
  damage_reduction_flat: "Flat damage reduction",
  will_destroyer_damage_pct: "Bonus damage (Will Destroyer)",
  regeneration_pct: "Lifesteal",
  regeneration_trigger_pct: "Lifesteal trigger chance",
  blessed_health: "Heal on attack proc",
};

const STAT_LABELS_SHORT: Record<string, string> = {
  patk: "PATK",
  matk: "MATK",
  pdef: "PDEF",
  mdef: "MDEF",
  crit: "crit",
  hp: "HP",
  crit_damage_pct: "crit dmg",
  dodge_pct: "dodge",
  damage_reduction_pct: "dmg reduction",
  damage_reduction_flat: "flat reduction",
  will_destroyer_damage_pct: "Will Destroyer",
  regeneration_pct: "lifesteal",
  regeneration_trigger_pct: "lifesteal proc",
  blessed_health: "heal proc",
};

function formatStatEntry(key: string, val: number, labels: Record<string, string>): string {
  const label = labels[key] ?? key.replace(/_/g, " ");
  if (key.endsWith("_pct")) {
    return `+${val}% ${label}`;
  }
  return `+${val} ${label}`;
}

export function formatAstralStatLines(astral: PlayerAstral): string[] {
  const lines: string[] = [];
  for (const [key, val] of Object.entries(astral.stats)) {
    if (typeof val !== "number" || val <= 0) continue;
    lines.push(formatStatEntry(key, val, STAT_LABELS));
  }
  if (lines.length === 0 && astral.description) {
    lines.push(astral.description);
  }
  return lines;
}

/** One-line effect summary for inventory chips (uses current level stats). */
export function getAstralPrimaryEffect(astral: PlayerAstral): string {
  const parts: string[] = [];
  for (const [key, val] of Object.entries(astral.stats)) {
    if (typeof val !== "number" || val <= 0) continue;
    parts.push(formatStatEntry(key, val, STAT_LABELS_SHORT));
  }
  if (parts.length === 0) {
    return astral.description?.trim() || "—";
  }
  return parts.join(" · ");
}

function formatStats(astral: PlayerAstral): string[] {
  return formatAstralStatLines(astral);
}

export function AstralDetailModal({
  astral,
  equipSlotCount = 0,
  onClose,
  onSell,
  onEquip,
  onUnequip,
  onUpgrade,
  actionLoading,
}: AstralDetailModalProps) {
  const rarityClass = RARITY_BORDER[astral.rarity] ?? RARITY_BORDER.gray;
  const glow = RARITY_GLOW[astral.rarity] ?? RARITY_GLOW.gray;
  const statLines = formatStats(astral);

  return (
    <ModalBackdrop onClose={onClose}>
      <div className={clsx("w-full max-w-sm bg-kingdom-purple border rounded-2xl p-4 space-y-3 shadow-lg", rarityClass, glow)}>
        <div className="flex items-start gap-3">
          <AstralOrb astral={astral} className="w-16 h-16 text-3xl" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-white truncate">{astral.name}</div>
            <div className={clsx("text-xs font-medium capitalize")}>
              {astral.rarity} · Lv.{astral.level}
              {astral.max_level > 1 && ` / ${astral.max_level}`}
            </div>
            <p className="text-xs text-gray-400 mt-1">{astral.description}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 text-xl">
            ✕
          </button>
        </div>

        {statLines.length > 0 && (
          <div className="bg-kingdom-dark/80 rounded-xl p-3 space-y-1">
            {statLines.map((line) => (
              <div key={line} className="text-sm text-gold-300">
                {line}
              </div>
            ))}
          </div>
        )}

        <div className="text-xs text-gray-400 space-y-0.5">
          <div>
            Sell: <FormattedNumber value={astral.sell_gold} mode="exact" /> gold
            {astral.sell_star_dust > 0 && (
              <>
                {" "}
                + <FormattedNumber value={astral.sell_star_dust} mode="exact" /> star dust
              </>
            )}
          </div>
          {astral.can_upgrade && astral.upgrade_star_dust_cost != null && (
            <div>
              Upgrade: <FormattedNumber value={astral.upgrade_star_dust_cost} mode="exact" /> star dust
              {astral.upgrade_gold_cost != null && astral.upgrade_gold_cost > 0 && (
                <>
                  {" "}
                  + <FormattedNumber value={astral.upgrade_gold_cost} mode="exact" /> gold
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {onUpgrade && astral.can_upgrade && (
            <button
              disabled={actionLoading}
              onClick={onUpgrade}
              className="flex-1 py-2.5 rounded-xl bg-violet-700/80 text-white text-sm font-bold disabled:opacity-50"
            >
              Upgrade
            </button>
          )}
          {onUnequip && astral.equipped_slot !== null && (
            <button
              disabled={actionLoading}
              onClick={onUnequip}
              className="flex-1 py-2.5 rounded-xl bg-gray-700 text-white text-sm font-bold disabled:opacity-50"
            >
              Unequip
            </button>
          )}
          {onSell && astral.equipped_slot === null && (
            <button
              disabled={actionLoading}
              onClick={onSell}
              className="flex-1 py-2.5 rounded-xl bg-amber-700/80 text-white text-sm font-bold disabled:opacity-50"
            >
              Sell
            </button>
          )}
          {onEquip && astral.equippable && astral.equipped_slot === null && equipSlotCount > 0 && (
            <button
              disabled={actionLoading}
              onClick={onEquip}
              className="flex-1 py-2.5 rounded-xl bg-kingdom-accent text-white text-sm font-bold disabled:opacity-50"
            >
              Equip
            </button>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}

function AstralOrb({
  astral,
  className,
}: {
  astral: PlayerAstral;
  className: string;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const rarityClass = RARITY_BORDER[astral.rarity] ?? RARITY_BORDER.gray;
  const fill = ASTRAL_RARITY_FILL[astral.rarity as keyof typeof ASTRAL_RARITY_FILL] ?? ASTRAL_RARITY_FILL.gray;
  const maskUrl = getAstralWhiteIconPath(astral.template_code);

  if (imgFailed) {
    return (
      <span
        className={clsx(
          "rounded-full border-2 flex items-center justify-center bg-kingdom-dark/60 shrink-0",
          rarityClass,
          className,
        )}
      >
        <span className="leading-none">✦</span>
      </span>
    );
  }

  return (
    <span
      className={clsx(
        "rounded-full border-2 shrink-0 overflow-hidden bg-kingdom-dark/40",
        rarityClass,
        className,
      )}
      style={{
        backgroundColor: fill,
        WebkitMaskImage: `url(${maskUrl})`,
        WebkitMaskSize: "88%",
        WebkitMaskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskImage: `url(${maskUrl})`,
        maskSize: "88%",
        maskRepeat: "no-repeat",
        maskPosition: "center",
      }}
    >
      <img src={maskUrl} alt="" className="sr-only" onError={() => setImgFailed(true)} />
    </span>
  );
}

export function AstralChip({
  astral,
  onClick,
  size = "md",
  labeled = false,
}: {
  astral: PlayerAstral;
  onClick?: () => void;
  size?: "sm" | "md";
  /** Name + rolled stats (Astral screen inventory). */
  labeled?: boolean;
}) {
  const sz = size === "sm" ? "w-9 h-9 text-base" : "w-14 h-14 text-2xl";
  const effect = labeled ? getAstralPrimaryEffect(astral) : null;

  const orb = <AstralOrb astral={astral} className={sz} />;

  if (labeled) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          "flex flex-col items-center gap-0.5 w-full min-w-0 px-0.5 py-0.5 text-center transition-transform",
          onClick ? "cursor-pointer hover:scale-[1.02]" : "cursor-default",
        )}
        title={`${astral.name} · ${effect}`}
      >
        {orb}
        <span className="text-[8px] leading-tight text-gray-400 truncate max-w-full w-full">{astral.name}</span>
        <span className="text-[9px] leading-snug text-gold-300/95 line-clamp-2 max-w-full w-full">{effect}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "transition-transform hover:scale-105",
        sz,
        onClick ? "cursor-pointer" : "cursor-default",
      )}
      title={astral.name}
    >
      <AstralOrb astral={astral} className={sz} />
    </button>
  );
}

export { RARITY_BORDER, RARITY_GLOW };
